import styled from '@emotion/styled';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import useUser from '../authentication/context/useUser';
import AppLayout from '../layout/AppLayout';
import { HeaderProjects as Header } from '../layout/Header';
import useModels from '../project/context/useModels';
import Button, { WhiteButton } from '../ui/Button';
import Loader from '../ui/Loader';
import Markdown from '../ui/Markdown';
import useConfiguration from '../useConfiguration';
import ModelBlock from './blocks/model';

const Layout = styled.div`
  display: flex;
  padding: 16px;
  flex-direction: column;
  min-height: calc(100vh - 64px);
  flex-grow: 1;
  gap: 20px;
  max-width: 1400px;
  width: 100%;
  background-color: #ffffff;
`;

const Top = styled.div`
  display: flex;
  flex-direction: row;
  min-height: 100px;
  gap: 20px;
`;

const TopRight = styled.div`
  flex-grow: 1;
  display: flex;
`;

const Bottom = styled.div`
  display: flex;
  flex-direction: row;
  min-height: 100px;
  flex-grow: 2;
  gap: 20px;
`;

const Dashboard = () => {
  const { models, loading } = useModels();
  const { user, seenFeature } = useUser();

  const siteModel = models?.find((model) => model.type === 'site');
  const gameModel = models?.find((model) => model.type === 'game');

  if (!models && loading) {
    return <Loader fullPage />;
  }

  return (
    <AppLayout bgColor="white" header={<Header />}>
      <AnnounceFeatures
        userSeenFeatures={user?.seenFeatures}
        onSeen={seenFeature}
      />
      <Helmet>
        <title>{`TRANSITION ENVIRONNEMENTALE DE LA FILIÈRE DU JEU VIDÉO`}</title>
      </Helmet>
      <Layout>
        <Top>
          {siteModel && (
            <TopRight>
              <ModelBlock
                model={siteModel}
                sortOrder="date"
                className="one-line"
              />
            </TopRight>
          )}
        </Top>
        <Bottom>
          {gameModel && <ModelBlock model={gameModel} sortOrder="date" />}
        </Bottom>
      </Layout>
    </AppLayout>
  );
};

const parseStringToArray = (input: string): string[] | null => {
  if (!input || input.length === 0) {
    return null;
  }

  // Remove <p> and </p> tags and trim spaces
  input = input.replace(/^<p>|<\/p>$/g, '').replace(/\s+/g, '');

  const formatRegex =
    /^([a-zA-Z0-9!@#$%^&*()_+=\-{}\[\]:;"'<>,.?/]+,)*[a-zA-Z0-9!@#$%^&*()_+=\-{}\[\]:;"'<>,.?/]+$/;

  if (!formatRegex.test(input)) {
    console.error(
      'Invalid format. The string should be a comma-separated list of words with letters and special characters.',
    );
    return null;
  }

  return input.split(',');
};

interface AnnounceFeaturesProps {
  userSeenFeatures: string[] | undefined;
  onSeen: (feature: string) => void;
}

const AnnounceFeatures = ({
  userSeenFeatures,
  onSeen,
}: AnnounceFeaturesProps) => {
  const { configuration } = useConfiguration();

  if (!userSeenFeatures) return null;

  const featuresToAnnounce = parseStringToArray(
    configuration['app.featuresToAnnounce'],
  );

  if (!featuresToAnnounce) return null;

  const newFeatures: string[] = featuresToAnnounce;
  const unseenFeatures = newFeatures.filter(
    (feature) => !userSeenFeatures.includes(feature),
  );

  if (unseenFeatures.length === 0) return null;

  return (
    <>
      {unseenFeatures.map((feature) => {
        return <Feature key={feature} feature={feature} onSeen={onSeen} />;
      })}
    </>
  );
};

interface FeatureProps {
  feature: string;
  onSeen: (feature: string) => void;
}

const Feature = ({ feature, onSeen }: FeatureProps) => {
  const [open, setOpen] = useState(true);
  const handleClose = () => setOpen(false);
  const handleValidate = async () => {
    await onSeen(feature);
    handleClose();
  };
  const { t, i18n } = useTranslation('common');

  const isTranslationsForKey = (key: string) => {
    return i18n.exists(key) && t(key) !== '';
  };

  if (
    !isTranslationsForKey(`common:features.${feature}.title`) ||
    !isTranslationsForKey(`common:features.${feature}.content`)
  ) {
    return null;
  }

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>{t(`common:features.${feature}.title`)}</DialogTitle>
      <DialogContent>
        <Markdown>{t(`common:features.${feature}.content`)}</Markdown>
      </DialogContent>
      <DialogActions>
        <WhiteButton onClick={handleClose}>{t('common:cancel')}</WhiteButton>
        <Button onClick={handleValidate}>{t('common:ok')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default Dashboard;
